import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    TablePagination,
    Grid,
    Card,
    CardContent,
    Avatar,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Chip,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Business as BranchIcon,
    LocationOn as LocationIcon,
    Person as ManagerIcon,
    Groups as TeamHeadIcon,
    ContactMail as ContactIcon,
    Phone as PhoneIcon,
    Badge as BadgeIcon,
    VpnKey as PasswordIcon,
    Visibility,
    VisibilityOff,
    Email as EmailIcon,
    AccountTree as DepartmentIcon,
    WorkOutline as DesignationIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from '../common/tableStyles';
import { SELECTED_MENU_PROPS } from '../StyledComponents/StyledComponent';
import {
    GET_MANAGERS, SAVE_MANAGER, UPDATE_MANAGER, DELETE_MANAGER,
    GET_BRANCHES, SAVE_BRANCH, UPDATE_BRANCH, DELETE_BRANCH,
    GET_LOCATIONS, SAVE_LOCATION, UPDATE_LOCATION, DELETE_LOCATION,
    GET_HR_CONTACTS, SAVE_HR_CONTACT, UPDATE_HR_CONTACT, DELETE_HR_CONTACT,
    GET_TEAM_HEADS, SAVE_TEAM_HEAD, UPDATE_TEAM_HEAD, DELETE_TEAM_HEAD,
    GET_DEPARTMENTS, SAVE_DEPARTMENT, UPDATE_DEPARTMENT, DELETE_DEPARTMENT,
    GET_DESIGNATIONS, SAVE_DESIGNATION, UPDATE_DESIGNATION, DELETE_DESIGNATION,
    GET_LITE_EMPLOYEE_DETAILS
} from '../../api';
import DepartmentDialog from './DepartmentDialog';
import DesignationDialog from './DesignationDialog';
import BranchDialog from './BranchDialog';

// ---- FORTHEYE soft-card tokens (shared with the employee portal) ----
const CARD_SX = {
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: '22px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff'; // primary blue
const GRADIENT_BTN = {
    textTransform: 'none', fontWeight: 700, borderRadius: '12px',
    background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
    '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};
// Soft tinted input styling (12px radius, light fill).
const INPUT_SX = { '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } };

const MasterData = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [branches, setBranches] = useState([]);
    const [locations, setLocations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fetchRequestId = useRef(0);

    // Dialog state
    const [open, setOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [currentItem, setCurrentItem] = useState({
        id: '',
        name: '',
        email: '',
        password: '',
        phone: '',
        employee_code: '',
        branch: '',
        branches: [],
        location: '',
        photo: '',
        address: '', city: '', state: '', postal_code: '',
        latitude: '', longitude: '', geofence_radius_meters: 100
    });

    const tabs = [
        { label: 'Managers', icon: <ManagerIcon />, type: 'MANAGER' },
        { label: 'Team Heads', icon: <TeamHeadIcon />, type: 'TEAM_HEAD' },
        { label: 'Branches', icon: <BranchIcon />, type: 'BRANCH' },
        { label: 'Locations', icon: <LocationIcon />, type: 'LOCATION' },
        { label: 'Departments', icon: <DepartmentIcon />, type: 'DEPARTMENT' },
        { label: 'Designations', icon: <DesignationIcon />, type: 'DESIGNATION' },
        { label: 'HR Contacts', icon: <ContactIcon />, type: 'HR_CONTACT' },
    ];

    const fetchDropdowns = async () => {
        try {
            const [bData, lData, dData, eData] = await Promise.all([
                GET_BRANCHES(), GET_LOCATIONS(), GET_DEPARTMENTS(), GET_LITE_EMPLOYEE_DETAILS()
            ]);
            setBranches(bData);
            setLocations(lData);
            setDepartments(dData);
            setEmployees(Array.isArray(eData) ? eData : (eData?.results || []));
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    };

    const fetchData = async () => {
        const requestId = ++fetchRequestId.current;
        setLoading(true);
        // Never render records from the previously selected master-data tab
        // while the next request is loading or if it fails.
        setData([]);
        try {
            let response;
            const type = tabs[activeTab].type;
            switch (type) {
                case 'MANAGER': response = await GET_MANAGERS(); break;
                case 'TEAM_HEAD': response = await GET_TEAM_HEADS(); break;
                case 'BRANCH': response = await GET_BRANCHES(); break;
                case 'LOCATION': response = await GET_LOCATIONS(); break;
                case 'DEPARTMENT': response = await GET_DEPARTMENTS(); break;
                case 'DESIGNATION': response = await GET_DESIGNATIONS(); break;
                case 'HR_CONTACT': response = await GET_HR_CONTACTS(); break;
                default: response = [];
            }
            if (requestId === fetchRequestId.current) {
                setData(response);
            }
        } catch (error) {
            if (requestId === fetchRequestId.current) {
                setData([]);
                toast.error('Failed to fetch data');
            }
            console.error(error);
        } finally {
            if (requestId === fetchRequestId.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setPage(0);
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditMode(true);
            setCurrentItem({
                ...item,
                password: '', // Don't show existing password
                branch: item.branch || '',
                branches: Array.isArray(item.branches)
                    ? item.branches
                    : (item.branch ? [item.branch] : []),
                location: item.location || '',
                employee_code: item.employee_code || '',
                phone: item.phone || '',
                photo: item.photo || ''
            });
        } else {
            setEditMode(false);
            setCurrentItem({
                id: '',
                name: '',
                email: '',
                password: '',
                phone: '',
                employee_code: '',
                branch: '',
                branches: [],
                location: '',
                photo: '',
                address: '', city: '', state: '', postal_code: '',
                latitude: '', longitude: '', geofence_radius_meters: 100
            });
        }
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setShowPassword(false);
    };

    const handleSave = async () => {
        if (!currentItem.name) {
            toast.error('Name is required');
            return;
        }

        const type = tabs[activeTab].type;
        if (type === 'HR_CONTACT') {
            if (!currentItem.email) {
                toast.error('Email is required');
                return;
            }
            if (!editMode && !currentItem.password) {
                toast.error('Password is required for new HR');
                return;
            }
        }

        setLoading(true);
        try {
            if (editMode) {
                switch (type) {
                    case 'MANAGER': await UPDATE_MANAGER(currentItem.id, { name: currentItem.name, email: currentItem.email || null }); break;
                    case 'TEAM_HEAD': await UPDATE_TEAM_HEAD(currentItem.id, { name: currentItem.name }); break;
                    case 'BRANCH': await UPDATE_BRANCH(currentItem.id, currentItem); break;
                    case 'LOCATION': await UPDATE_LOCATION(currentItem.id, { name: currentItem.name }); break;
                    case 'HR_CONTACT':
                        await UPDATE_HR_CONTACT(currentItem.id, {
                            ...currentItem,
                            // Only include password if it's been updated
                            ...(currentItem.password && { password: currentItem.password })
                        });
                        break;
                    default:
                        break;
                }
                toast.success('Updated successfully');
            } else {
                switch (type) {
                    case 'MANAGER': await SAVE_MANAGER({ name: currentItem.name, email: currentItem.email || null }); break;
                    case 'TEAM_HEAD': await SAVE_TEAM_HEAD({ name: currentItem.name }); break;
                    case 'BRANCH': await SAVE_BRANCH(currentItem); break;
                    case 'LOCATION': await SAVE_LOCATION({ name: currentItem.name }); break;
                    case 'HR_CONTACT': await SAVE_HR_CONTACT(currentItem); break;
                    default: break;
                }
                toast.success('Saved successfully');
            }
            handleCloseDialog();
            fetchData();
        } catch (error) {
            toast.error('Failed to save data');
        } finally {
            setLoading(false);
        }
    };

    const handleOrganizationSave = async (form) => {
        const type = tabs[activeTab].type;
        setLoading(true);
        try {
            if (type === 'DEPARTMENT') {
                if (editMode) await UPDATE_DEPARTMENT(currentItem.id, form);
                else await SAVE_DEPARTMENT(form);
            } else if (type === 'DESIGNATION') {
                if (editMode) await UPDATE_DESIGNATION(currentItem.id, form);
                else await SAVE_DESIGNATION(form);
            }
            toast.success(editMode ? 'Updated successfully' : 'Saved successfully');
            handleCloseDialog();
            await fetchData();
            if (type === 'DEPARTMENT') setDepartments(await GET_DEPARTMENTS());
        } catch (error) {
            const details = error?.response?.data;
            const message = details ? Object.values(details).flat().join(' ') : 'Failed to save data';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handleBranchSave = async (form) => {
        setLoading(true);
        try {
            if (editMode) await UPDATE_BRANCH(currentItem.id, form);
            else await SAVE_BRANCH(form);
            toast.success(editMode ? 'Updated successfully' : 'Saved successfully');
            handleCloseDialog();
            await fetchData();
            setBranches(await GET_BRANCHES());
        } catch (error) {
            const details = error?.response?.data;
            toast.error(details ? Object.values(details).flat().join(' ') : 'Failed to save branch');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;
        setLoading(true);
        try {
            const type = tabs[activeTab].type;
            switch (type) {
                case 'MANAGER': await DELETE_MANAGER(id); break;
                case 'TEAM_HEAD': await DELETE_TEAM_HEAD(id); break;
                case 'BRANCH': await DELETE_BRANCH(id); break;
                case 'LOCATION': await DELETE_LOCATION(id); break;
                case 'DEPARTMENT': await DELETE_DEPARTMENT(id); break;
                case 'DESIGNATION': await DELETE_DESIGNATION(id); break;
                case 'HR_CONTACT': await DELETE_HR_CONTACT(id); break;
                default: break;
            }
            toast.success(type === 'DEPARTMENT' || type === 'DESIGNATION'
                ? 'Deactivated successfully' : 'Deleted successfully');
            fetchData();
        } catch (error) {
            toast.error('Failed to delete data');
        } finally {
            setLoading(false);
        }
    };

    const renderHRGrid = () => (
        <Grid container spacing={3}>
            {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                <Grid item xs={12} sm={6} lg={4} key={item.id}>
                    <Card
                        elevation={0}
                        sx={{
                            ...CARD_SX,
                            p: 0,
                            '&:hover': { boxShadow: '0 12px 28px rgba(17,24,39,0.08)', transform: 'translateY(-2px)' },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar
                                    src={item.photo}
                                    sx={{ width: 64, height: 64, mr: 2, bgcolor: `${ACCENT}14`, color: ACCENT, fontWeight: 800 }}
                                >
                                    {item.name.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>{item.name}</Typography>
                                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                        ID: {item.employee_code || 'N/A'}
                                    </Typography>
                                </Box>
                                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                                    <IconButton onClick={() => handleOpenDialog(item)} size="small" sx={{ color: ACCENT, bgcolor: `${ACCENT}14`, '&:hover': { bgcolor: `${ACCENT}22` } }}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(item.id)} size="small" sx={{ color: '#ef4444', bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fee2e2' } }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Divider sx={{ mb: 2, borderColor: '#eef0f4' }} />

                            <Stack spacing={1.5}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <EmailIcon sx={{ color: '#6b7280', fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ color: '#1e293b' }}>{item.email}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <PhoneIcon sx={{ color: '#6b7280', fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ color: '#1e293b' }}>{item.phone || 'No phone'}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                    {(item.branches?.length ? item.branches : (item.branch ? [item.branch] : [])).map((branch) => (
                                        <Chip key={branch} label={branch} size="small" sx={{ fontWeight: 700, bgcolor: `${ACCENT}14`, color: ACCENT, border: 'none' }} />
                                    ))}
                                    {item.location && <Chip label={item.location} size="small" sx={{ fontWeight: 700, bgcolor: '#f3f4f6', color: '#6b7280', border: 'none' }} />}
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    const renderTable = () => {
        const type = tabs[activeTab].type;
        const isManager = type === 'MANAGER';
        const isOrganizationMaster = type === 'DEPARTMENT' || type === 'DESIGNATION';
        return (
        <Paper elevation={0} sx={CARD_SX}>
            <TableContainer component={Paper} elevation={0} sx={{ ...TABLE_PAPER_SX, boxShadow: 'none', border: 'none' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...HEAD_CELL_SX }}>Name</TableCell>
                            {isOrganizationMaster && <TableCell sx={{ ...HEAD_CELL_SX }}>Code</TableCell>}
                            {type === 'DEPARTMENT' && <TableCell sx={{ ...HEAD_CELL_SX }}>Parent / Head</TableCell>}
                            {type === 'DESIGNATION' && <TableCell sx={{ ...HEAD_CELL_SX }}>Department / Level</TableCell>}
                            {isOrganizationMaster && <TableCell sx={{ ...HEAD_CELL_SX }}>Employees</TableCell>}
                            {isOrganizationMaster && <TableCell sx={{ ...HEAD_CELL_SX }}>Status</TableCell>}
                            {isManager && <TableCell sx={{ ...HEAD_CELL_SX }}>Email</TableCell>}
                            <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'right' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                            <TableRow key={item.id} sx={{ ...TABLE_ROW_SX }}>
                                <TableCell sx={{ ...BODY_CELL_SX }}>{item.name}</TableCell>
                                {isOrganizationMaster && <TableCell sx={{ ...BODY_CELL_SX }}>{item.code}</TableCell>}
                                {type === 'DEPARTMENT' && (
                                    <TableCell sx={{ ...BODY_CELL_SX }}>
                                        <Typography variant="body2">{item.parent_department_name || 'Top level'}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {item.department_head_name || 'No head assigned'}
                                        </Typography>
                                    </TableCell>
                                )}
                                {type === 'DESIGNATION' && (
                                    <TableCell sx={{ ...BODY_CELL_SX }}>
                                        <Typography variant="body2">{item.department_name}</Typography>
                                        <Typography variant="caption" color="text.secondary">Level {item.level}</Typography>
                                    </TableCell>
                                )}
                                {isOrganizationMaster && <TableCell sx={{ ...BODY_CELL_SX }}>{item.employee_count}</TableCell>}
                                {isOrganizationMaster && (
                                    <TableCell sx={{ ...BODY_CELL_SX }}>
                                        <Chip size="small" label={item.is_active ? 'Active' : 'Inactive'}
                                            color={item.is_active ? 'success' : 'default'} />
                                    </TableCell>
                                )}
                                {isManager && (
                                    <TableCell sx={{ ...BODY_CELL_SX }}>
                                        {item.email
                                            ? item.email
                                            : <span style={{ color: '#ef4444', fontStyle: 'italic' }}>— not set —</span>}
                                    </TableCell>
                                )}
                                <TableCell sx={{ ...BODY_CELL_SX }} align="right">
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <IconButton onClick={() => handleOpenDialog(item)} size="small" sx={{ color: ACCENT, bgcolor: `${ACCENT}14`, '&:hover': { bgcolor: `${ACCENT}22` } }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton onClick={() => handleDelete(item.id)} size="small" sx={{ color: '#ef4444', bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fee2e2' } }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
        );
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            {/* Header card */}
            <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <BadgeIcon />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                                Master Data
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                Manage organizational entities and HR credentials.
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        sx={{ ...GRADIENT_BTN, px: 3, py: 1 }}
                    >
                        Add {tabs[activeTab].label.slice(0, -1)}
                    </Button>
                </Stack>
            </Paper>

            {/* Tabs card */}
            <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 0.5, sm: 1 }, mb: { xs: 2, md: 3 }, overflow: 'hidden' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    sx={{
                        '& .MuiTabs-indicator': { backgroundColor: ACCENT, height: 3, borderRadius: '3px' },
                        '& .MuiTab-root': { minHeight: 64, px: 4, fontWeight: 700, textTransform: 'none', color: '#6b7280' },
                        '& .Mui-selected': { color: `${ACCENT} !important` },
                    }}
                >
                    {tabs.map((tab, index) => (
                        <Tab
                            key={index}
                            label={tab.label}
                            icon={tab.icon}
                            iconPosition="start"
                        />
                    ))}
                </Tabs>
            </Paper>

            {loading && data.length === 0 ? (
                <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>
            ) : data.length === 0 ? (
                <Paper elevation={0} sx={{ ...CARD_SX, py: 8, textAlign: 'center' }}>
                    <Stack alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <BadgeIcon />
                        </Box>
                        <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No records found in this category.</Typography>
                    </Stack>
                </Paper>
            ) : (
                <>
                    {tabs[activeTab].type === 'HR_CONTACT' ? renderHRGrid() : renderTable()}
                    <TablePagination
                        component="div"
                        count={data.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </>
            )}

            {!['BRANCH', 'DEPARTMENT', 'DESIGNATION'].includes(tabs[activeTab].type) && (
            <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '22px' } }}>
                <DialogTitle sx={{ fontWeight: 800, p: 3, pb: 1, color: '#111827' }}>
                    {editMode ? 'Update' : 'Register New'} {tabs[activeTab].label.slice(0, -1)}
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Full Name"
                                fullWidth
                                value={currentItem.name}
                                onChange={(e) => setCurrentItem({ ...currentItem, name: e.target.value })}
                                sx={INPUT_SX}
                            />
                        </Grid>

                        {tabs[activeTab].type === 'MANAGER' && (
                            <Grid item xs={12}>
                                <TextField
                                    label="Email (for leave notifications)"
                                    fullWidth
                                    value={currentItem.email}
                                    onChange={(e) => setCurrentItem({ ...currentItem, email: e.target.value })}
                                    placeholder="manager@adityatrading.com"
                                    helperText="Leave requests naming this manager are emailed here. If blank, the system falls back to guessing by name."
                                    sx={INPUT_SX}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        )}

                        {tabs[activeTab].type === 'HR_CONTACT' && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Email Address"
                                        fullWidth
                                        value={currentItem.email}
                                        onChange={(e) => setCurrentItem({ ...currentItem, email: e.target.value })}
                                        sx={INPUT_SX}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label={editMode ? "New Password (leave blank to keep current)" : "Login Password"}
                                        fullWidth
                                        type={showPassword ? 'text' : 'password'}
                                        value={currentItem.password}
                                        onChange={(e) => setCurrentItem({ ...currentItem, password: e.target.value })}
                                        helperText={
                                            editMode
                                                ? (currentItem.has_password
                                                    ? "Password is set. Leave blank to keep the current password."
                                                    : "No password is set. Enter a new password to enable login.")
                                                : "Required for login access."
                                        }
                                        FormHelperTextProps={{
                                            sx: {
                                                color: editMode
                                                    ? (currentItem.has_password ? '#16803c' : '#b45309')
                                                    : 'text.secondary',
                                                fontWeight: editMode ? 700 : 400,
                                            }
                                        }}
                                        sx={INPUT_SX}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PasswordIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Employee ID / Code"
                                        fullWidth
                                        value={currentItem.employee_code}
                                        onChange={(e) => setCurrentItem({ ...currentItem, employee_code: e.target.value })}
                                        sx={INPUT_SX}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BadgeIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Phone Number"
                                        fullWidth
                                        value={currentItem.phone}
                                        onChange={(e) => setCurrentItem({ ...currentItem, phone: e.target.value })}
                                        sx={INPUT_SX}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth sx={INPUT_SX}>
                                        <InputLabel>Branches</InputLabel>
                                        <Select
                                            multiple
                                            value={currentItem.branches || []}
                                            label="Branches"
                                            MenuProps={SELECTED_MENU_PROPS}
                                            onChange={(e) => {
                                                const value = typeof e.target.value === 'string'
                                                    ? e.target.value.split(',')
                                                    : e.target.value;
                                                setCurrentItem({
                                                    ...currentItem,
                                                    branches: value,
                                                    branch: value[0] || ''
                                                });
                                            }}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => <Chip key={value} label={value} size="small" />)}
                                                </Box>
                                            )}
                                        >
                                            {branches.map((b) => (
                                                <MenuItem key={b.id} value={b.name}>{b.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth sx={INPUT_SX}>
                                        <InputLabel>Location</InputLabel>
                                        <Select
                                            value={currentItem.location}
                                            label="Location"
                                            MenuProps={SELECTED_MENU_PROPS}
                                            onChange={(e) => setCurrentItem({ ...currentItem, location: e.target.value })}
                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            {locations.map((l) => (
                                                <MenuItem key={l.id} value={l.name}>{l.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Profile Photo URL"
                                        fullWidth
                                        value={currentItem.photo}
                                        onChange={(e) => setCurrentItem({ ...currentItem, photo: e.target.value })}
                                        placeholder="https://example.com/photo.jpg"
                                        sx={INPUT_SX}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={handleCloseDialog} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" disableElevation sx={{ ...GRADIENT_BTN, px: 4 }}>
                        {editMode ? 'Update Details' : 'Create User'}
                    </Button>
                </DialogActions>
            </Dialog>
            )}
            <BranchDialog
                open={open && tabs[activeTab].type === 'BRANCH'}
                item={editMode ? currentItem : null}
                loading={loading}
                onClose={handleCloseDialog}
                onSave={handleBranchSave}
            />
            <DepartmentDialog
                open={open && tabs[activeTab].type === 'DEPARTMENT'}
                item={editMode ? currentItem : null}
                departments={departments}
                employees={employees}
                loading={loading}
                onClose={handleCloseDialog}
                onSave={handleOrganizationSave}
            />
            <DesignationDialog
                open={open && tabs[activeTab].type === 'DESIGNATION'}
                item={editMode ? currentItem : null}
                departments={departments}
                loading={loading}
                onClose={handleCloseDialog}
                onSave={handleOrganizationSave}
            />
        </Box>
    );
};

export default MasterData;
